import type { Project } from '@swchat/protocol';
import { useEffect, useRef, useState } from 'react';
import { Avatar, Badge, Button, Modal, Tabs } from '../../design';
import { projectOptions } from '../../lib/display';
import { newChatUsers, projectPreselect, switchProjectPicks } from '../../lib/group';
import { useClient } from '../../lib/hooks';
import { errorText } from '../format';

const MODES = [
  { id: 'direct' as const, label: '1-1' },
  { id: 'group' as const, label: 'Nhóm', testId: 'mode-group' },
];

/** Tạo chat 1-1 hoặc nhóm (B1), nhóm gắn dự án (E2: thành viên dự án xếp trước, chọn sẵn). */
export function NewChatDialog({
  projects,
  initialProject,
  onClose,
  onCreated,
}: {
  projects: Project[];
  initialProject?: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const client = useClient();
  const [mode, setMode] = useState<'direct' | 'group'>(initialProject ? 'group' : 'direct');
  const [picked, setPicked] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [, refresh] = useState(0);
  /** E2: nhóm gắn dự án — gợi ý (xếp trước, chọn sẵn) thành viên dự án. */
  const [projectId, setProjectId] = useState(initialProject ?? '');
  const [projectUsers, setProjectUsers] = useState<string[]>([]);
  /** Người đang được chọn sẵn theo dự án (chưa ai tick/bỏ tay) — đổi dự án chỉ thay nhóm này, giữ người tick tay (Q1#3). */
  const autoPicked = useRef<string[]>([]);

  useEffect(() => void client.loadUsers().then(() => refresh((n) => n + 1)), [client]);
  useEffect(() => {
    setProjectUsers([]);
    const applyAuto = (next: string[]) => {
      const prev = autoPicked.current;
      setPicked((p) => {
        const out = switchProjectPicks(p, prev, next);
        // Chỉ người được THÊM do dự án mới mới tính là tự động; người đã tick tay giữ nguyên là tick tay (Q1#3).
        const drop = new Set(prev);
        const manual = new Set(p.filter((id) => !drop.has(id)));
        autoPicked.current = next.filter((id) => !manual.has(id));
        return out;
      });
    };
    if (!projectId) {
      applyAuto([]);
      return;
    }
    let alive = true;
    void client
      .projectMembers(projectId)
      .then((ids) => {
        if (!alive) return;
        setProjectUsers(ids);
        applyAuto(projectPreselect(ids, client.getUsers(), client.userId));
      })
      .catch((e) => alive && setError(errorText(e)));
    return () => {
      alive = false;
    };
  }, [client, projectId]);

  const inProject = new Set(projectUsers);
  const users = newChatUsers(client.getUsers(), client.userId, filter, projectUsers);

  async function create(userIds: string[]) {
    setBusy(true);
    setError(undefined);
    try {
      const id = mode === 'direct' ? await client.openDirect(userIds[0]!) : await client.createGroup(name.trim(), userIds, projectId || undefined);
      // Chat 1-1 đã có nhưng thiết bị này (vừa khôi phục/liên kết) chưa được thêm vào nhóm MLS: báo rõ thay vì đóng hộp
      // thoại mà không mở được gì. Thiết bị được thêm khi một thiết bị khác của mình hoặc của người kia trực tuyến.
      if (!client.hasConversation(id)) {
        setError('Đã có chat 1-1 với người này nhưng thiết bị này chưa được thêm vào. Hãy chờ một thiết bị khác của bạn hoặc của người kia trực tuyến rồi thử lại.');
        setBusy(false);
        return;
      }
      onCreated(id);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  }

  return (
    <Modal title="Cuộc trò chuyện mới" onClose={onClose} testId="new-chat-dialog">
      <Tabs label="Loại cuộc trò chuyện" variant="pill" items={MODES} value={mode} onChange={setMode} />
      {mode === 'group' && (
        <input className="ws-input" data-testid="group-name" placeholder="Tên nhóm" aria-label="Tên nhóm" value={name} onChange={(e) => setName(e.target.value)} maxLength={128} />
      )}
      {mode === 'group' && projects.length > 0 && (
        <select className="ws-input" data-testid="group-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label="Dự án">
          {projectOptions(projects, 'Không gắn dự án').map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
      <input className="ws-input" placeholder="Tìm người…" aria-label="Tìm người" value={filter} onChange={(e) => setFilter(e.target.value)} />
      {error && <p className="ws-error">{error}</p>}
      <ul className="ws-pick-list">
        {users.map((u) => (
          <li key={u.id} data-testid="user-option" data-user-id={u.id} data-user-name={u.display_name}>
            {mode === 'direct' ? (
              <button type="button" className="ws-pick" disabled={busy || !u.has_identity} onClick={() => void create([u.id])}>
                <Avatar name={u.display_name} seed={u.id} size="sm" />
                <span className="ws-pick-name">{u.display_name}</span>
                {!u.has_identity && <span className="ws-muted ws-small"> · chưa kích hoạt</span>}
              </button>
            ) : (
              <label className="ws-pick ws-check">
                <input
                  type="checkbox"
                  disabled={!u.has_identity}
                  checked={picked.includes(u.id)}
                  onChange={(e) => {
                    // Tick/bỏ tay → không còn là lựa chọn tự động theo dự án (đổi dự án không đụng tới).
                    autoPicked.current = autoPicked.current.filter((x) => x !== u.id);
                    const checked = e.target.checked;
                    setPicked((p) => (checked ? [...p, u.id] : p.filter((x) => x !== u.id)));
                  }}
                />
                <Avatar name={u.display_name} seed={u.id} size="sm" />
                <span className="ws-pick-name">{u.display_name}</span>
                {inProject.has(u.id) && (
                  <span className="ws-in-project" data-testid="in-project">
                    <Badge tone="accent">thuộc dự án</Badge>
                  </span>
                )}
                {!u.has_identity && <span className="ws-muted ws-small"> · chưa kích hoạt</span>}
              </label>
            )}
          </li>
        ))}
        {!users.length && <li className="ws-muted">Không có người dùng (chỉ hiện người đã đăng nhập SW Chat ít nhất một lần).</li>}
      </ul>
      {mode === 'group' && (
        <Button variant="primary" data-testid="create-group" disabled={busy || !name.trim() || !picked.length} onClick={() => void create(picked)}>
          {busy ? 'Đang tạo…' : `Tạo nhóm (${picked.length + 1} người)`}
        </Button>
      )}
    </Modal>
  );
}
